import { CacheProvider, EmotionCache } from "@emotion/react"
import { SessionProvider } from "next-auth/react"
import type { AppProps } from "next/app"
import { createTheme } from "@mui/material/styles"
import ThemeProvider from "@mui/styles/ThemeProvider"
import ErrorScreen from "@web/components/ErrorScreen/ErrorScreen"
import "@web/styles/global.css"
import * as React from "react"
import createEmotionCache from "../utils/createEmotionCache"
import { ApolloClientProvider } from "@web/hooks/useApolloClient"
import { Session } from "next-auth"
import Head from "next/head"
import { BillingProvider } from "@web/context/BillingContext"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { WindowMessageProvider } from "@web/context/WindowMessageContext"
import { ErrorBoundary } from "@sentry/nextjs"
import localFont from "next/font/local"
import { TestingContextProvider } from "@web/context/TestingContext"

const metaCaps = localFont({ src: "../../public/font/Meta-Caps Regular.otf", variable: "--font-family-meta-caps" })

const theme = createTheme({
  typography: {
    fontFamily: ["Source Sans Pro", "sans-serif"].join(","),
  },
})

export const META_TITLE = "Are You A Recruiter? Save 4 Hours Per Day! "
export const META_DESCRIPTION =
  "Free open source AI automates 60% of a Recruiter's day and makes Sourcing candidates 3x faster."

const queryClient = new QueryClient()

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache()

interface TomboAppProps {
  emotionCache?: EmotionCache
  session: Session
  appSyncApiKey?: string | null
}

export default function TomboApp({
  Component,
  pageProps: { session, emotionCache, appSyncApiKey, ...pageProps },
}: AppProps<TomboAppProps>) {
  emotionCache ||= clientSideEmotionCache

  React.useEffect(() => {
    // Remove the server-side injected CSS.
    const jssStyles = document.querySelector("#jss-server-side")
    if (jssStyles) {
      jssStyles?.parentElement?.removeChild(jssStyles)
    }
  }, [])

  return (
    <CacheProvider value={emotionCache}>
      <ErrorBoundary fallback={<ErrorScreen onOkClicked={() => document.location.reload()} />}>
        <SessionProvider session={session}>
          <TestingContextProvider appSyncApiKey={appSyncApiKey}>
            <ApolloClientProvider>
              <WindowMessageProvider>
                <QueryClientProvider client={queryClient}>
                  <BillingProvider>
                    <ThemeProvider theme={theme}>
                      <main className={`${metaCaps.className}`}>
                        <Head>
                          <title>{META_TITLE}</title>
                          <meta property="og:title" content={META_TITLE} />

                          <meta property="og:description" content={META_DESCRIPTION} />
                          <meta name="description" content={META_DESCRIPTION} />

                          <meta property="og:image" content="/images/og/Tombo-robot.jpg" />
                        </Head>
                        <Component {...pageProps} />
                      </main>
                    </ThemeProvider>
                  </BillingProvider>
                </QueryClientProvider>
              </WindowMessageProvider>
            </ApolloClientProvider>
          </TestingContextProvider>
        </SessionProvider>
      </ErrorBoundary>
    </CacheProvider>
  )
}
